import { useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  Button,
  Card,
  CardContent,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@mlm/ui';
import {
  getTenantFeatures,
  listBranches,
  listExpiringLots,
  type ExpiryBucket,
  type StockLot,
} from '../lib/api';
import { ExpiryBadge } from '../components/inventory/expiry-badge';
import {
  BUCKET_LABEL,
  costLabel,
  locationLabel,
  lotName,
  lotValue,
} from '../components/inventory/lot-bits';
import { LotWriteOffDialog } from '../components/inventory/lot-write-off-dialog';

type Filter = 'expired' | 'within_7' | 'within_30' | 'all';

const FILTERS: Array<{ key: Filter; label: string }> = [
  { key: 'expired', label: 'Expired' },
  { key: 'within_7', label: 'Within 7 days' },
  { key: 'within_30', label: 'Within 30 days' },
  { key: 'all', label: 'All dated stock' },
];

/** Buckets a filter shows: each one includes everything more urgent than it. */
const SHOWN: Record<Filter, ExpiryBucket[]> = {
  expired: ['expired'],
  within_7: ['expired', 'within_7'],
  within_30: ['expired', 'within_7', 'within_30'],
  all: ['expired', 'within_7', 'within_30', 'later'],
};

/**
 * Expiring stock: every open stock lot that carries an expiry date, soonest
 * first, with a one-click write-off of that exact lot. The aging view of the
 * stock-lots feature; a lot with no date never appears here.
 */
export function ExpiringStockPage() {
  const featuresQuery = useQuery({ queryKey: ['tenant-features'], queryFn: getTenantFeatures });
  if (featuresQuery.isLoading) return <p className="text-sm text-muted-foreground">Loading…</p>;
  if (featuresQuery.data?.inventory_lots_enabled !== true) {
    return (
      <Card>
        <CardContent className="py-10 text-center text-sm text-muted-foreground">
          Stock lots are turned off for this tenant. Turn on <strong>Track stock in lots</strong> in
          Settings → System to record a cost and an expiry date for each delivery and see what is
          about to expire here.
        </CardContent>
      </Card>
    );
  }
  return <ExpiringStock showLocation={featuresQuery.data?.item_locator_enabled === true} />;
}

function ExpiringStock({ showLocation }: { showLocation: boolean }) {
  const [branchId, setBranchId] = useState<string>('all');
  const [filter, setFilter] = useState<Filter>('within_30');
  const [writeOff, setWriteOff] = useState<StockLot | null>(null);

  const branchesQuery = useQuery({ queryKey: ['admin-branches'], queryFn: listBranches });
  const lotsQuery = useQuery({
    queryKey: ['admin-stock-lots', 'expiring', branchId],
    queryFn: () => listExpiringLots({ branchId: branchId === 'all' ? undefined : branchId }),
  });

  const all = useMemo(() => lotsQuery.data?.lots ?? [], [lotsQuery.data]);
  const counts = useMemo(() => {
    const c: Record<ExpiryBucket, number> = {
      expired: 0,
      within_7: 0,
      within_30: 0,
      later: 0,
      none: 0,
    };
    for (const l of all) c[l.bucket] += 1;
    return c;
  }, [all]);
  const rows = all.filter((l) => SHOWN[filter].includes(l.bucket));
  const countFor = (f: Filter) => SHOWN[f].reduce((sum, b) => sum + counts[b], 0);

  return (
    <div className="space-y-4">
      <div>
        <h1 className="text-2xl font-semibold">Expiring stock</h1>
        <p className="text-sm text-muted-foreground">
          Deliveries that carry an expiry date, soonest first. Write off a lot here and it comes off
          the shelf count and into the books as a write-off.
        </p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Select value={branchId} onValueChange={setBranchId}>
          <SelectTrigger className="h-9 w-56">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All branches</SelectItem>
            {(branchesQuery.data ?? []).map((b) => (
              <SelectItem key={b.id} value={b.id}>
                {b.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
        <div className="inline-flex flex-wrap rounded-md border p-0.5 text-xs">
          {FILTERS.map((f) => (
            <button
              key={f.key}
              type="button"
              onClick={() => setFilter(f.key)}
              className={
                'rounded px-3 py-1 transition-colors ' +
                (filter === f.key
                  ? 'bg-primary text-primary-foreground'
                  : 'text-muted-foreground hover:text-foreground')
              }
            >
              {f.label} ({countFor(f.key)})
            </button>
          ))}
        </div>
      </div>

      {lotsQuery.isLoading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : rows.length === 0 ? (
        <Card>
          <CardContent className="py-10 text-center text-sm text-muted-foreground">
            {all.length === 0
              ? 'No stock with an expiry date yet. Type an expiry date when you receive a delivery, or give the product a shelf life.'
              : `Nothing ${filter === 'expired' ? 'has expired' : `expires ${BUCKET_LABEL[filter === 'all' ? 'later' : filter].toLowerCase()}`}.`}
          </CardContent>
        </Card>
      ) : (
        <Card>
          <CardContent className="overflow-x-auto p-0">
            <table className="w-full text-sm">
              <thead className="border-b text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-4 py-2">Product</th>
                  <th className="px-4 py-2">Branch</th>
                  <th className="px-4 py-2">Lot</th>
                  <th className="px-4 py-2">Expires</th>
                  {showLocation && <th className="px-4 py-2">Location</th>}
                  <th className="px-4 py-2 text-right">Left</th>
                  <th className="px-4 py-2 text-right">Unit cost</th>
                  <th className="px-4 py-2 text-right">Value</th>
                  <th className="px-4 py-2" />
                </tr>
              </thead>
              <tbody>
                {rows.map((l) => (
                  <tr key={l.id} className="border-b last:border-0 hover:bg-muted/30">
                    <td className="px-4 py-2">
                      {l.productName}
                      {l.variantName && (
                        <span className="text-muted-foreground"> ({l.variantName})</span>
                      )}
                      <div className="font-mono text-xs text-muted-foreground">{l.sku}</div>
                    </td>
                    <td className="px-4 py-2">
                      <Link to={`/branches/${l.branchId}`} className="hover:underline">
                        {l.branchName}
                      </Link>
                    </td>
                    <td className="px-4 py-2 font-mono text-xs">{lotName(l)}</td>
                    <td className="px-4 py-2">
                      <div className="tabular-nums">{l.expiresOn}</div>
                      <ExpiryBadge bucket={l.bucket} daysLeft={l.daysLeft} />
                    </td>
                    {showLocation && (
                      <td className="px-4 py-2 text-xs text-muted-foreground">
                        {locationLabel(l.locationAisle, l.locationRack)}
                      </td>
                    )}
                    <td className="px-4 py-2 text-right tabular-nums">
                      {l.qtyRemaining.toLocaleString()} {l.unitLabel?.trim() || 'pcs'}
                    </td>
                    <td className="px-4 py-2 text-right tabular-nums">{costLabel(l.unitCost)}</td>
                    <td className="px-4 py-2 text-right tabular-nums">{lotValue(l) ?? '-'}</td>
                    <td className="px-4 py-2 text-right">
                      <Button
                        size="sm"
                        variant="outline"
                        className="text-destructive"
                        onClick={() => setWriteOff(l)}
                      >
                        Write off
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <LotWriteOffDialog
        lot={writeOff}
        onOpenChange={(open) => {
          if (!open) setWriteOff(null);
        }}
      />
    </div>
  );
}
